import React, { useEffect, useState } from 'react';

import { Button, Progress, notification } from 'antd';
import { useNavigate } from 'react-router-dom';

import { hasAnsweredQuizz, normalizeQuizzEmail, saveQuizzAnswers } from 'src/api/quizz';
import { glLettersLogo } from 'src/assets/images';
import { Input } from 'src/components';
import { emailValidation, ucFirst } from 'src/utils';

import { QUIZZ_TITLE, QuizzOptionKey, getQuizzScore, quizzQuestions } from './questions';
import styles from './Quizz.module.css';

type StepT = 'register' | 'quizz' | 'done' | 'already';

const STORAGE_KEY = 'gl_quizz_completed';

const getStoredCompletion = () => {
   try {
      return localStorage.getItem(STORAGE_KEY);
   } catch {
      return null;
   }
};

const setStoredCompletion = (email: string) => {
   try {
      localStorage.setItem(STORAGE_KEY, email);
   } catch {
      // storage unavailable (private mode), Firestore check still applies
   }
};

const Quizz = () => {
   const navigate = useNavigate();
   const [api, contextHolder] = notification.useNotification();

   const [step, setStep] = useState<StepT>(getStoredCompletion() ? 'already' : 'register');
   const [name, setName] = useState('');
   const [email, setEmail] = useState('');
   const [nameEmpty, setNameEmpty] = useState(false);
   const [emailEmpty, setEmailEmpty] = useState(false);

   const [current, setCurrent] = useState(0);
   const [answers, setAnswers] = useState<Record<number, QuizzOptionKey>>({});
   const [startedAt, setStartedAt] = useState<Date | null>(null);
   const [loading, setLoading] = useState(false);

   const question = quizzQuestions[current];
   const isLast = current === quizzQuestions.length - 1;
   const emailIsValid = email !== '' && emailValidation(email);

   useEffect(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
   }, [step, current]);

   const showError = (description: string) => {
      api.error({ message: QUIZZ_TITLE, description, placement: 'topLeft', key: 'quizzkey' });
   };

   const handleStart = async (e: React.FormEvent) => {
      e.preventDefault();
      if (!name.trim() || !emailIsValid) {
         setNameEmpty(!name.trim());
         setEmailEmpty(!email);
         return;
      }

      setLoading(true);
      const { answered, success } = await hasAnsweredQuizz(email);
      setLoading(false);

      if (success !== 'true') return showError('No pudimos validar tu información. Intenta de nuevo más tarde.');
      if (answered) {
         setStoredCompletion(normalizeQuizzEmail(email));
         return setStep('already');
      }

      setStartedAt(new Date());
      setStep('quizz');
   };

   const handleSubmit = async () => {
      setLoading(true);
      const finishedAt = new Date();
      const { correctCount, totalQuestions, grade } = getQuizzScore(answers);

      const { success, alreadyAnswered } = await saveQuizzAnswers({
         name: name.trim(),
         email,
         answers: quizzQuestions.map(({ id, question: text }) => ({ questionId: id, question: text, answer: answers[id] })),
         correctCount,
         totalQuestions,
         grade,
         startedAt,
         finishedAt,
         durationSeconds: startedAt ? Math.round((finishedAt.getTime() - startedAt.getTime()) / 1000) : null,
      });
      setLoading(false);

      if (alreadyAnswered) {
         setStoredCompletion(normalizeQuizzEmail(email));
         return setStep('already');
      }
      if (success !== 'true') return showError('No pudimos guardar tus respuestas. Intenta de nuevo.');

      setStoredCompletion(normalizeQuizzEmail(email));
      setStep('done');
   };

   const renderMessage = (title: string, description: string) => (
      <div className={styles.center}>
         <img src={glLettersLogo} width={70} height={70} alt='gl-letters-logo' />
         <br />
         <br />
         <h2>{title}</h2>
         <p className={styles.subtitle}>{description}</p>
         <br />
         <Button size='large' type='primary' ghost onClick={() => navigate('/', { replace: true })}>
            Ir al inicio
         </Button>
      </div>
   );

   return (
      <>
         {contextHolder}
         <div className={styles.wrapper}>
            <div className={styles.card}>
               {step === 'register' && (
                  <form onSubmit={handleStart}>
                     <div className={styles.center}>
                        <img src={glLettersLogo} width={70} height={70} alt='gl-letters-logo' />
                        <br />
                        <br />
                        <h2>{QUIZZ_TITLE}</h2>
                     </div>
                     <p className={styles.subtitle}>Ingresa tus datos para comenzar. Solo podrás responder el quiz una vez.</p>
                     <div className={styles.meta}>
                        <span>{quizzQuestions.length} preguntas</span>
                        <span>20–25 minutos</span>
                        <span>Nivel intermedio–avanzado</span>
                     </div>
                     <Input
                        id='name'
                        name='name'
                        size='large'
                        placeholder='Nombre completo*'
                        value={name}
                        onChange={(e) => {
                           setName(e.target.value ? ucFirst(e.target.value) : '');
                           setNameEmpty(e.target.value === '');
                        }}
                        onBlur={() => setNameEmpty(name.trim() === '')}
                        typeColor={nameEmpty ? 'error' : 'default'}
                        status={nameEmpty ? 'error' : ''}
                        text={nameEmpty ? 'Este es un campo requerido' : ''}
                     />
                     <Input
                        id='email'
                        name='email'
                        size='large'
                        placeholder='Correo electrónico*'
                        value={email}
                        onChange={(e) => {
                           setEmail(e.target.value.trim());
                           setEmailEmpty(e.target.value === '');
                        }}
                        onBlur={() => setEmailEmpty(email === '')}
                        typeColor={emailEmpty || (email && !emailIsValid) ? 'error' : 'default'}
                        status={emailEmpty || (email && !emailIsValid) ? 'error' : ''}
                        text={emailEmpty ? 'Este es un campo requerido' : email && !emailIsValid ? 'Formato de correo inválido' : ''}
                     />
                     <div className={styles.center}>
                        <Button
                           htmlType='submit'
                           size='large'
                           type='primary'
                           ghost
                           loading={loading}
                           disabled={!name.trim() || !emailIsValid}
                           style={{ minWidth: '50%' }}>
                           Comenzar quiz
                        </Button>
                     </div>
                  </form>
               )}

               {step === 'quizz' && (
                  <>
                     <p className={styles.progressLabel}>
                        Pregunta {current + 1} de {quizzQuestions.length}
                     </p>
                     <Progress percent={Math.round(((current + 1) / quizzQuestions.length) * 100)} showInfo={false} strokeColor='#866F5E' />
                     <h3 className={styles.question}>
                        {question.id}. {question.question}
                     </h3>
                     <div className={styles.options} role='radiogroup'>
                        {question.options.map((option) => {
                           const selected = answers[question.id] === option.key;
                           return (
                              <button
                                 key={option.key}
                                 type='button'
                                 role='radio'
                                 aria-checked={selected}
                                 className={`${styles.option} ${selected ? styles.optionSelected : ''}`}
                                 onClick={() => setAnswers((prev) => ({ ...prev, [question.id]: option.key }))}>
                                 <span className={styles.optionKey}>{option.key}</span>
                                 <span>{option.text}</span>
                              </button>
                           );
                        })}
                     </div>
                     <div className={styles.actions}>
                        <Button size='large' disabled={current === 0 || loading} onClick={() => setCurrent((prev) => prev - 1)}>
                           Anterior
                        </Button>
                        {isLast ? (
                           <Button
                              size='large'
                              type='primary'
                              loading={loading}
                              disabled={Object.keys(answers).length !== quizzQuestions.length}
                              onClick={handleSubmit}>
                              Enviar respuestas
                           </Button>
                        ) : (
                           <Button size='large' type='primary' ghost disabled={!answers[question.id]} onClick={() => setCurrent((prev) => prev + 1)}>
                              Siguiente
                           </Button>
                        )}
                     </div>
                  </>
               )}

               {step === 'done' &&
                  renderMessage('¡Quiz completado correctamente!', `Gracias ${name.trim()}, tus respuestas fueron registradas con éxito.`)}

               {step === 'already' && renderMessage('Ya respondiste este quiz', 'Nuestros registros indican que ya completaste el quiz. Solo se permite una participación.')}
            </div>
         </div>
      </>
   );
};

export default Quizz;
